import React, { useState, useEffect } from "react";
import Icon from "../../../../components/AppIcon";

const GlobalProviderModal = ({ provider, onClose, onSave }) => {
  const isEdit = !!provider;

  const [formData, setFormData] = useState({
    type: 0, // 0 = HR, 1 = Till
    name: "",
    code: "",
    logoUrl: "",
    isActive: true,
    defaultConfiguration: "",
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (provider) {
      setFormData({
        type: provider.type,
        name: provider.name || "",
        code: provider.code || "",
        logoUrl: provider.logoUrl || "",
        isActive: provider.isActive ?? true,
        defaultConfiguration: provider.defaultConfiguration || "",
      });
    }
  }, [provider]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!/^[A-Za-z0-9]+$/.test(formData.code)) {
      newErrors.code = "Code must be alphanumeric";
    }

    if (formData.defaultConfiguration) {
      try {
        JSON.parse(formData.defaultConfiguration);
      } catch {
        newErrors.defaultConfiguration = "Invalid JSON format";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold">
            {isEdit ? "Edit Provider" : "Add Integration Provider"}
          </h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          <div className="space-y-4">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Provider Type <span className="text-error">*</span>
              </label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={formData.type === 0}
                    onChange={() => handleChange("type", 0)}
                    disabled={isEdit}
                    className="radio"
                  />
                  <span className="flex items-center gap-1">
                    <Icon name="Users" size={16} />
                    HR System
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={formData.type === 1}
                    onChange={() => handleChange("type", 1)}
                    disabled={isEdit}
                    className="radio"
                  />
                  <span className="flex items-center gap-1">
                    <Icon name="CreditCard" size={16} />
                    Till / POS
                  </span>
                </label>
              </div>
              {isEdit && (
                <p className="text-xs text-muted-foreground mt-1">
                  Provider type cannot be changed after creation
                </p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Provider Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={`input w-full ${errors.name ? "input-error" : ""}`}
                placeholder="e.g., Sona HR"
              />
              {errors.name && (
                <p className="text-error text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* Code */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Provider Code <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) =>
                  handleChange(
                    "code",
                    e.target.value.replace(/[^A-Za-z0-9]/g, ""),
                  )
                }
                className={`input w-full font-mono ${errors.code ? "input-error" : ""}`}
                placeholder="e.g., Sona"
                disabled={isEdit}
              />
              {errors.code && (
                <p className="text-error text-sm mt-1">{errors.code}</p>
              )}
              {isEdit && (
                <p className="text-xs text-muted-foreground mt-1">
                  Code cannot be changed after creation
                </p>
              )}
            </div>

            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Logo URL
              </label>
              <input
                type="text"
                value={formData.logoUrl}
                onChange={(e) => handleChange("logoUrl", e.target.value)}
                className="input w-full"
                placeholder="/assets/integrations/provider-logo.png"
              />
              {formData.logoUrl && (
                <div className="mt-2 p-2 bg-muted/50 rounded-lg inline-block">
                  <img
                    src={formData.logoUrl}
                    alt="Logo preview"
                    className="h-8 w-auto object-contain"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              )}
            </div>

            {/* Active */}
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <label className="font-medium">Active</label>
                <p className="text-sm text-muted-foreground">
                  Make this provider available to tenants
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Default Configuration */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Default Configuration (JSON)
              </label>
              <textarea
                value={formData.defaultConfiguration}
                onChange={(e) =>
                  handleChange("defaultConfiguration", e.target.value)
                }
                className={`input w-full min-h-[100px] font-mono text-sm ${errors.defaultConfiguration ? "input-error" : ""}`}
                placeholder='{"api_endpoint": "https://api.example.com/v1/"}'
              />
              {errors.defaultConfiguration && (
                <p className="text-error text-sm mt-1">
                  {errors.defaultConfiguration}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Default configuration template for new tenant integrations
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Check" size={16} />
                  {isEdit ? "Save Changes" : "Create Provider"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GlobalProviderModal;
